import { Button, Collapse, Flex, Text } from '@chakra-ui/react';
import styled from '@emotion/styled';
import { ReactComponent as ChevronDownIcon } from 'assets/icons/chevron_down.svg';
import { ReactComponent as ChevronUpIcon } from 'assets/icons/chevron_up.svg';
import { useAppSelector } from 'modules/Editor/store';
import { selectBrandIdx } from 'modules/Editor/store/editorSettingsSelector';
import { useBrand, useUser } from 'modules/User';
import { BrandOptionDropdown } from 'modules/User/components/BrandOptionDropdown';
import { ReactElement, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { TEXT_TAG_TO_STYLES_MAP } from 'widgets/TextBasedWidgets/common/TextBasedWidgets.config';

import { SideSettingDivider } from '../../../SideMenuSettings/SidemenuSettingsDivider';
import { SimpleBrandKitButton } from '../BrandKitMenu/BrandKitTab/SimpleEditBrandKitButton';
import { ElementsPackTypes } from '../ElementsMenu/ElementsMenu.types';
import { TextButton } from './TextButton';
import { TextElementsMenu } from './TextElementsMenu';
import { Heading } from './TextWidgetMenuHeading';

// for smaller screens with scroll - the collapse hides the overflow: https://github.com/chakra-ui/chakra-ui/issues/2966
const CollapseTransition = styled(Collapse)`
  overflow: ${({ in: isEnabled }) => (isEnabled ? 'initial !important' : 'hidden')};
`;

export const TextWidgetMenu = (): ReactElement => {
  const [isCollapsed, setIsCollapsed] = useState<boolean>(true);
  const [selectedElementPack, setSelectedElementPack] = useState<ElementsPackTypes | null>(null);
  const { isFreeUser, isPremiumUser, isEducationUser, userType } = useUser();
  const focusButtonRef = useRef<HTMLButtonElement>(null);
  const textWidgetMenuRef = useRef<HTMLDivElement>(null);

  const { t } = useTranslation('editor_widget_menu', { useSuspense: false, keyPrefix: 'sideMenu' });

  const toggleCollapse = () => {
    const newIsCollapsed = !isCollapsed;
    setIsCollapsed(newIsCollapsed);
  };

  const onCollapseAnimationComplete = () => {
    // focus the first of the newly visible buttons
    if (!isCollapsed) focusButtonRef.current?.focus();
  };

  const brandIdx = useAppSelector(selectBrandIdx);
  const { data: brandData } = useBrand();
  const { brandConfig = { editable: false } } = useBrand({
    brandId: brandData?.[brandIdx]?.id,
  });

  // Display & use the users selected brand fonts
  const selectedBrand = brandData?.[brandIdx];
  const titleFont = selectedBrand?.title?.fontFamily;
  const headingFont = selectedBrand?.subtitle?.fontFamily;
  const paragraphFont = selectedBrand?.text?.fontFamily;

  const hasBrandKit = brandData && brandData.length > 1 && !isEducationUser;
  const hasBrandEditPermission = (isFreeUser || isPremiumUser || brandConfig.editable) && !isEducationUser;

  return (
    <Flex
      direction='column'
      h='full'
      overflowX='hidden'
      overflowY={selectedElementPack ? 'hidden' : 'auto'}
      p={4}
      paddingRight={selectedElementPack ? 0 : 4}
      ref={textWidgetMenuRef}
    >
      {!selectedElementPack && (
        <>
          {hasBrandKit && (
            <Flex alignItems='center' justifyContent='space-between' mb={4}>
              <Text fontSize='sm' fontWeight='semibold'>
                {t('stylesMenu.brandKit')}
              </Text>
              <BrandOptionDropdown w='11.375rem' triggerProps={{ h: 8, w: '11.375rem' }} />
            </Flex>
          )}

          <Flex justify='space-between' alignItems='center' mb={4}>
            <Heading />
            {hasBrandEditPermission && (
              <SimpleBrandKitButton hasEditPermission={hasBrandEditPermission} ariaLabel={t('stylesMenu.brandKit')} />
            )}
          </Flex>

          {/* WIDGET STYLES */}
          <Flex direction='column' gap='8px'>
            <TextButton
              type={'heading 1'}
              customFontFamily={titleFont}
              style={TEXT_TAG_TO_STYLES_MAP['heading 1']}
              text={t('textMenu.buttons.h1')}
            />
            <TextButton
              type={'heading 2'}
              customFontFamily={headingFont}
              style={TEXT_TAG_TO_STYLES_MAP['heading 2']}
              text={t('textMenu.buttons.h2')}
            />
            <TextButton
              type={'heading 3'}
              customFontFamily={headingFont}
              style={TEXT_TAG_TO_STYLES_MAP['heading 3']}
              text={t('textMenu.buttons.h3')}
            />

            {/* COLLAPSED STYLES */}
            <CollapseTransition in={!isCollapsed} onAnimationComplete={onCollapseAnimationComplete} animateOpacity>
              <Flex gap='8px' direction='column'>
                <TextButton
                  type={'heading 4'}
                  buttonRef={focusButtonRef}
                  customFontFamily={headingFont}
                  style={TEXT_TAG_TO_STYLES_MAP['heading 4']}
                  text={t('textMenu.buttons.h4')}
                />
                <TextButton
                  type={'heading 5'}
                  customFontFamily={headingFont}
                  style={TEXT_TAG_TO_STYLES_MAP['heading 5']}
                  text={t('textMenu.buttons.h5')}
                />
                <TextButton
                  type={'heading 6'}
                  customFontFamily={headingFont}
                  style={TEXT_TAG_TO_STYLES_MAP['heading 6']}
                  text={t('textMenu.buttons.h6')}
                />
              </Flex>
            </CollapseTransition>

            <TextButton
              type={'paragraph'}
              customFontFamily={paragraphFont}
              style={TEXT_TAG_TO_STYLES_MAP['paragraph']}
              text={t('textMenu.buttons.paragraph')}
            />

            <Button
              onClick={toggleCollapse}
              rightIcon={isCollapsed ? <ChevronDownIcon /> : <ChevronUpIcon />}
              variant='ghost'
              fontSize='sm'
              fontWeight='medium'
              color='upgrade.blue.500'
              size='sm'
              sx={{ '& path': { stroke: 'var(--vg-colors-upgrade-blue-500)' } }}
              maxW='fit-content'
            >
              {isCollapsed ? t('textMenu.showMore') : t('textMenu.showLess')}
            </Button>
          </Flex>
          <SideSettingDivider mt={3} />
        </>
      )}
      <TextElementsMenu
        userType={userType}
        selectedElementPack={selectedElementPack}
        setSelectedElementPack={setSelectedElementPack}
        textWidgetMenuRef={textWidgetMenuRef}
      />
    </Flex>
  );
};
